import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, UsersIcon, HandshakeIcon, MessageSquareIcon, EyeIcon, RepeatIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
type Lane = 'Medical' | 'Commercial' | 'Shared';
interface JourneyStep {
  icon: typeof UsersIcon;
  lane: Lane;
  title: string;
  desc: string;
}
const JOURNEY_STEPS: JourneyStep[] = [{
  icon: UsersIcon,
  lane: 'Medical',
  title: 'Congress engagement',
  desc: 'Field teams meet HCPs at congress moments already rich with attention.'
}, {
  icon: HandshakeIcon,
  lane: 'Medical',
  title: 'MSL face-to-face visit',
  desc: 'An MSL follows up in person with tailored scientific depth.'
}, {
  icon: MessageSquareIcon,
  lane: 'Commercial',
  title: 'Snak follow-up',
  desc: 'A short interactive Snak reinforces the conversation right after the visit.'
}, {
  icon: EyeIcon,
  lane: 'Shared',
  title: 'Shared insight',
  desc: 'Engagement and choices from the Snak feed back to commercial and medical alike.'
}, {
  icon: RepeatIcon,
  lane: 'Commercial',
  title: 'Next touchpoint',
  desc: 'Insight shapes the next congress, visit, or campaign moment.'
}];
const LANE_STYLES: Record<Lane, string> = {
  Medical: 'bg-[var(--accent)]/10 text-[var(--accent)]',
  Commercial: 'bg-[var(--brand)]/10 text-[var(--brand)]',
  Shared: 'bg-[var(--fg)]/10 text-[var(--fg)]'
};
export function SlideOmnichannelJourney({}: SlideProps) {
  return <InteriorSlide surface data-id="element-386" data-mp="26m504lkmbq2">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.8vw,1.75rem)] flex-shrink-0" data-id="element-387" data-mp="3kjaekmteove0">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-388" data-mp="3alm8x1u9i3h2">
          One integrated journey
        </p>
        <h2 className="max-w-5xl text-[clamp(2rem,3.4vw,3.3rem)] font-extrabold leading-tight tracking-[-0.04em] text-[var(--fg)]" data-id="element-389" data-mp="1l3fnbefpwu1x">
          Commercial and medical,{' '}
          <span className="text-[var(--brand)]" data-id="element-390" data-mp="1gbfs1zalepbo">one continuous flow.</span>
        </h2>
        <p className="mt-3 max-w-4xl text-[clamp(0.9rem,1.15vw,1.05rem)] leading-relaxed text-[var(--muted)]" data-id="element-391" data-mp="1cbndn5yipoye">
          Congress engagement, face-to-face MSL visits, and follow-up touchpoints
          connect with commercial Snak campaigns—not two separate tracks, but one
          journey the HCP experiences as a whole.
        </p>
      </motion.header>

      <div className="flex min-h-0 flex-1 flex-col justify-center gap-4" data-id="element-392" data-mp="1v13rsw34lfxa">
        <div className="flex items-stretch gap-2 xl:gap-3" data-id="element-393" data-mp="1q2isqzzm12af">
          {JOURNEY_STEPS.map((step, index) => {
          const Icon = step.icon;
          return <React.Fragment key={step.title} data-id="element-394" data-mp="jnrm0fnt2n8h" data-mpi={index}>
                <motion.article initial={{
              opacity: 0,
              y: 22
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + index * 0.1,
              duration: 0.55
            }} className="flex flex-1 flex-col rounded-2xl border border-[var(--brand)]/10 bg-white p-4 shadow-sm" data-id="element-395" data-mp="emu5e7ig2193">
                  <span className={`inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${LANE_STYLES[step.lane]}`} data-id="element-396" data-mp="eg1o6f5oybsq">
                    {step.lane}
                  </span>
                  <span className="mt-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--surface)] text-[var(--fg)]" data-id="element-397" data-mp="s8835xx3ycql">
                    <Icon className="h-5 w-5" aria-hidden="true" data-id="element-398" data-mp="x8ngiphe3k8k" />
                  </span>
                  <p className="mt-3 text-sm font-bold leading-snug text-[var(--fg)]" data-id="element-399" data-mp="126ovozbp2zer">
                    {step.title}
                  </p>
                  <p className="mt-1.5 text-xs leading-relaxed text-[var(--muted)]" data-id="element-400" data-mp="33yk4n76kgpta">
                    {step.desc}
                  </p>
                </motion.article>
                {index < JOURNEY_STEPS.length - 1 && <div className="flex flex-shrink-0 items-center justify-center" data-id="element-401" data-mp="tbzszqb7gmut">
                    <ArrowRightIcon className="h-4 w-4 text-[var(--brand)]/40" aria-hidden="true" data-id="element-402" data-mp="17apox2magghl" />
                  </div>}
              </React.Fragment>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.9,
        duration: 0.5
      }} className="flex items-center justify-center gap-2 text-xs font-semibold text-[var(--muted)]" data-id="element-403" data-mp="ejxx0hc9ae02">
          <RepeatIcon className="h-3.5 w-3.5 text-[var(--brand)]" aria-hidden="true" data-id="element-404" data-mp="jkc3vw24fhwi" />
          Insight closes the loop back into the next congress, visit, or campaign.
        </motion.div>
      </div>
    </InteriorSlide>;
}